/* pe-buttons-with-icons.js — the <pe-button-list> strip controller for
 * sections/pe-buttons-with-icons.liquid (R2-8, 2026-08-02). Moved verbatim from the
 * section's former inline <script> so the ~2KB stops re-shipping in every document
 * (and per instance) and loads deferred like the theme's other section elements.
 * Late definition is safe: connectedCallback only queries children that exist in
 * the server markup, and customElements upgrade runs it on define. */

if (!customElements.get('pe-button-list')) {
  customElements.define(
    'pe-button-list',
    class PeButtonList extends HTMLElement {
      connectedCallback() {
        this.items = this.querySelector('.pe-bwi__items');
        if (!this.items) return;
        this.prevButton = this.querySelector('[data-pe-prev]');
        this.nextButton = this.querySelector('[data-pe-next]');

        this._onScroll = this.updateReach.bind(this);
        this.items.addEventListener('scroll', this._onScroll, { passive: true });
        window.addEventListener('resize', this._onScroll);

        if (this.prevButton) this.prevButton.addEventListener('click', () => this.scrollByAmount(-1));
        if (this.nextButton) this.nextButton.addEventListener('click', () => this.scrollByAmount(1));

        this.initDrag();

        // Theme editor: bring the selected button into view.
        if (window.Shopify && Shopify.designMode) {
          document.addEventListener('shopify:block:select', (event) => {
            const item = event.target.closest('.pe-bwi__item');
            if (item && this.contains(item)) {
              item.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
            }
          });
        }

        this.updateReach();
      }

      disconnectedCallback() {
        this.items.removeEventListener('scroll', this._onScroll);
        window.removeEventListener('resize', this._onScroll);
      }

      // Click-and-drag to scroll (mouse only — touch scrolls natively).
      // Mirrors the drag behaviour of paex's native slider used by "PE - Collection cards".
      initDrag() {
        const el = this.items;
        let down = false;
        let dragging = false;
        let startX = 0;
        let startScroll = 0;

        // Stop the browser's native drag-and-drop of links/images, which would
        // otherwise steal the pointer mid-drag and stall the scroll.
        el.addEventListener('dragstart', (event) => event.preventDefault());

        el.addEventListener('mousedown', (event) => {
          // Only left button, and only when the strip actually overflows.
          if (event.button !== 0 || el.scrollWidth <= el.clientWidth) return;
          // Prevent text selection and native link/image dragging during the drag.
          event.preventDefault();
          down = true;
          dragging = false;
          startX = event.pageX;
          startScroll = el.scrollLeft;
        });

        document.addEventListener('mousemove', (event) => {
          if (!down) return;
          const dx = event.pageX - startX;
          if (!dragging && Math.abs(dx) > 3) {
            dragging = true;
            el.classList.add('is-grabbing');
          }
          if (dragging) el.scrollLeft = startScroll - dx;
        });

        document.addEventListener('mouseup', () => {
          if (!down) return;
          down = false;
          el.classList.remove('is-grabbing');
          if (dragging) {
            // Swallow the click that follows a drag so links don't navigate.
            const blockClick = (event) => {
              event.preventDefault();
              event.stopPropagation();
            };
            el.addEventListener('click', blockClick, { capture: true, once: true });
            window.setTimeout(() => el.removeEventListener('click', blockClick, { capture: true }), 0);
          }
          dragging = false;
        });

        // Cancel a drag that leaves the window.
        document.addEventListener('mouseleave', () => {
          if (down) {
            down = false;
            dragging = false;
            el.classList.remove('is-grabbing');
          }
        });
      }

      scrollByAmount(direction) {
        const amount = this.items.clientWidth * 0.8;
        this.items.scrollBy({ left: direction * amount, behavior: 'smooth' });
      }

      updateReach() {
        const el = this.items;
        const max = el.scrollWidth - el.clientWidth;
        let reach = 'none';
        if (max > 1) {
          const left = Math.abs(el.scrollLeft);
          if (left <= 1) reach = 'begin';
          else if (left >= max - 1) reach = 'end';
          else reach = 'progress';
        }
        this.dataset.reach = reach;
        if (this.prevButton) this.prevButton.disabled = reach === 'none' || reach === 'begin';
        if (this.nextButton) this.nextButton.disabled = reach === 'none' || reach === 'end';
      }
    }
  );
}
